import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, faPlay, faCheck, faExclamationTriangle, 
  faTimesCircle, faBan, faCalendarTimes 
} from '@fortawesome/free-solid-svg-icons';

interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  deadline?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  size = 'md', 
  showIcon = true,
  deadline 
}) => {
  const getStatusConfig = (status: string) => {
    const now = new Date();
    const deadlineDate = deadline ? new Date(deadline) : null;
    const isOverdue = deadlineDate && deadlineDate < now;
    
    switch (status) {
      case 'draft':
        return {
          label: 'Entwurf',
          icon: faClock,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-300'
        };
      
      case 'sent':
        if (isOverdue) {
          return {
            label: 'Überfällig',
            icon: faExclamationTriangle,
            bgColor: 'bg-red-100',
            textColor: 'text-red-700',
            borderColor: 'border-red-300'
          };
        }
        return {
          label: 'Ausstehend',
          icon: faClock,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-700',
          borderColor: 'border-yellow-300'
        };
      
      case 'in_progress':
        if (isOverdue) {
          return {
            label: 'Überfällig (In Bearbeitung)',
            icon: faExclamationTriangle,
            bgColor: 'bg-orange-100',
            textColor: 'text-orange-700',
            borderColor: 'border-orange-300'
          };
        }
        return {
          label: 'In Bearbeitung',
          icon: faPlay,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-300'
        };
      
      case 'completed':
        return {
          label: 'Erledigt',
          icon: faCheck,
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          borderColor: 'border-green-300'
        };
      
      case 'overdue':
        return {
          label: 'Überfällig',
          icon: faExclamationTriangle,
          bgColor: 'bg-red-100',
          textColor: 'text-red-700',
          borderColor: 'border-red-300'
        };
      
      case 'expired':
        return {
          label: 'Abgelaufen',
          icon: faTimesCircle,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-600',
          borderColor: 'border-gray-300'
        };
      
      case 'not_sent_this_year':
        return {
          label: 'Noch nicht gesendet',
          icon: faCalendarTimes,
          bgColor: 'bg-purple-100',
          textColor: 'text-purple-700',
          borderColor: 'border-purple-300'
        };
      
      default:
        return {
          label: status,
          icon: faClock,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-300'
        };
    }
  };

  const getSizeClasses = (size: string) => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-2 py-1 text-xs',
          icon: 'text-xs mr-1'
        };
      case 'lg':
        return {
          container: 'px-4 py-2 text-base',
          icon: 'text-sm mr-2'
        };
      default: // md
        return {
          container: 'px-3 py-1.5 text-sm',
          icon: 'text-xs mr-1.5'
        };
    }
  };

  const config = getStatusConfig(status);
  const sizeClasses = getSizeClasses(size);

  return (
    <span 
      className={`
        inline-flex items-center rounded-full font-medium border
        ${config.bgColor} ${config.textColor} ${config.borderColor}
        ${sizeClasses.container}
      `}
      title={`Status: ${config.label}${deadline ? ` • Deadline: ${new Date(deadline).toLocaleDateString('de-DE')}` : ''}`}
    >
      {showIcon && (
        <FontAwesomeIcon 
          icon={config.icon} 
          className={sizeClasses.icon}
        />
      )}
      {config.label}
    </span>
  );
};

export default StatusBadge;